// Calendar-date maths for payment schedules. Dates are plain YYYY-MM-DD
// strings and are shifted in UTC, so the result never depends on the
// viewer's time zone. (Building a local-midnight Date and calling
// toISOString() moved every IST date one day earlier.)
export function addDaysToISODate(isoDate, days) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(isoDate || ""));
  if (!match || !Number.isFinite(Number(days))) return null;
  const value = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + Number(days)));
  return value.toISOString().slice(0, 10);
}

// Today's date in the viewer's calendar, as YYYY-MM-DD.
export function todayISODate(now = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

// "Sat, 5 Dec 2026" — weekday included so customers can plan the payment.
export function formatTimelineDate(isoDate) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(isoDate || ""));
  if (!match) return null;
  const value = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return value.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
}

export function formatPaymentDueLabel(milestone, eventDate) {
  const offset = milestone?.offset_days;
  if (offset == null) return milestone?.due_label || "At booking confirmation";
  const days = Math.abs(Number(offset));
  if (Number(offset) === 0) return "On the event day";
  return Number(offset) < 0
    ? `${days} day${days === 1 ? "" : "s"} before the event`
    : `${days} day${days === 1 ? "" : "s"} after the event`;
}

export function formatPaymentMilestoneLabel(milestone, index = 0, eventDate) {
  if (milestone?.milestone === "booking_advance" || milestone?.offset_days == null) {
    return "Booking advance";
  }
  const date = milestone?.due_date || (eventDate && Number.isFinite(Number(milestone.offset_days))
    ? addDaysToISODate(eventDate, milestone.offset_days)
    : null);
  if (date) {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(date));
    if (match) {
      return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])))
        .toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
    }
  }
  return milestone?.label && !/^event date\s*[-+−]/i.test(milestone.label)
    ? milestone.label
    : `Payment ${index + 1}`;
}

// Mirror of backend financial_engine.fold_overdue_milestones: pre-event
// instalments already due by `todayIso` are added to the upfront row so the
// customer pays them at checkout instead of starting with an overdue balance.
export function foldOverdueMilestones(schedule, eventDate, todayIso) {
  const rows = (schedule || []).map((row) => ({ ...row }));
  const upfront = rows.find((row) => row.offset_days == null);
  if (!eventDate || !todayIso || !upfront) return rows;
  let folded = 0;
  const kept = rows.filter((row) => {
    if (row === upfront || row.offset_days == null || Number(row.offset_days) > 0) return true;
    const due = addDaysToISODate(eventDate, row.offset_days);
    if (due && due <= todayIso) {
      folded += Number(row.percent || 0);
      return false;
    }
    return true;
  });
  if (folded) upfront.percent = Math.round((Number(upfront.percent || 0) + folded) * 10000) / 10000;
  return kept;
}

// Mirror of backend financial_engine.apply_minimum_upfront: raise the upfront
// row to `minPercent`, pulling from the nearest instalments first and keeping
// whatever is left of them (so 90% on 30/40/20/10 gives 90/10, not 100).
export function applyMinimumUpfront(schedule, minPercent) {
  const rows = (schedule || []).map((row) => ({ ...row }));
  const upfront = rows.find((row) => row.offset_days == null);
  if (!upfront || !(minPercent > 0)) return rows;
  const round4 = (n) => Math.round(n * 10000) / 10000;
  let need = round4(Math.min(Number(minPercent), 100) - Number(upfront.percent || 0));
  if (need <= 0) return rows;
  const ordered = rows
    .filter((row) => row.offset_days != null)
    .sort((a, b) => {
      const groupA = Number(a.offset_days) <= 0 ? 0 : 1;
      const groupB = Number(b.offset_days) <= 0 ? 0 : 1;
      return groupA - groupB || Number(a.offset_days) - Number(b.offset_days);
    });
  for (const row of ordered) {
    if (need <= 0) break;
    const take = Math.min(Number(row.percent || 0), need);
    row.percent = round4(Number(row.percent || 0) - take);
    upfront.percent = round4(Number(upfront.percent || 0) + take);
    need = round4(need - take);
  }
  return rows.filter((row) => row.offset_days == null || Number(row.percent) > 0);
}

// Mirror of backend financial_engine.resolve_payment_schedule — the schedule
// checkout will actually create, from admin settings (rows + instant_book_rules).
export function resolvePaymentSchedule(schedule, eventDate, todayIso, hoursToEvent, rules = {}) {
  const resolved = foldOverdueMilestones(schedule, eventDate, todayIso);
  if (hoursToEvent == null) return resolved;
  if (hoursToEvent <= Number(rules.very_short_window_hours ?? 48)) {
    return applyMinimumUpfront(resolved, Number(rules.very_short_window_min_pct ?? 100));
  }
  if (hoursToEvent <= Number(rules.standard_window_days ?? 7) * 24) {
    return applyMinimumUpfront(resolved, Number(rules.short_window_min_before_event_pct ?? 90));
  }
  return resolved;
}
